import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronDown, Check, Calendar as CalendarIcon, Clock, ArrowLeft, Image as ImageIcon, Award, Globe } from 'lucide-react';
import { Dropdown, MenuProps, DatePicker, TimePicker, Select, Modal, message, Spin } from 'antd';
import { Editor } from '@tinymce/tinymce-react';
import dayjs, { Dayjs } from 'dayjs';
import { searchStaff, useStaffOptions, type StaffOption } from '../useStaff';
import { UserAvatar } from './UserAvatar';
import {
  AUDIENCE_LABEL, BADGES, htmlToText,
  type Audience, type Me, type Post, type PostImage,
} from '../socialStore';
import { feedApi, type PostPatch } from '../socialApi';

export type ComposerMode = 'post' | 'recognition';

interface CreatePostModalProps {
  open: boolean;
  onClose: () => void;
  me: Me;
  mode?: ComposerMode;
  /** Bài đang sửa (chỉ tác giả) */
  editing?: Post | null;
  /** Mở hộp chọn ảnh ngay khi bật modal (nút "Ảnh/Video") */
  pickImages?: boolean;
  /** Nội dung soạn sẵn (vd. lời chúc sinh nhật); khi có thì không dùng / không ghi đè bản nháp */
  initialHtml?: string;
  /** Gọi sau khi đăng / sửa thành công, kèm bài API trả về */
  onPosted?: (post: Post) => void;
}

const MAX_IMAGES = 6;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const AUDIENCES: { key: Audience; icon: typeof Globe; bg: string }[] = [
  { key: 'organization', icon: Globe, bg: 'bg-blue-500' },
];

export default function CreatePostModal(props: CreatePostModalProps) {
  if (!props.open) return null;
  // key: mỗi lần mở là một phiên soạn mới, không giữ nội dung cũ
  return <Composer key={`${props.editing?.id ?? 'new'}-${props.mode}`} {...props} />;
}

// Bản nháp bài mới (không gồm ảnh cho nhẹ): đóng modal hay tải lại trang vẫn viết tiếp được
interface Draft { title: string; content: string; audiences: Audience[]; people: number[]; badge: string; commentsLocked: boolean }
const draftKey = (meId: string, mode: ComposerMode) => `social_draft_${meId}_${mode}`;
export function loadDraft(meId: string, mode: ComposerMode): Draft | null {
  try { return JSON.parse(localStorage.getItem(draftKey(meId, mode)) || 'null'); } catch { return null; }
}
const saveDraft = (meId: string, mode: ComposerMode, d: Draft | null) => {
  try {
    if (d) localStorage.setItem(draftKey(meId, mode), JSON.stringify(d));
    else localStorage.removeItem(draftKey(meId, mode));
  } catch { /* bộ nhớ đầy: bỏ qua nháp */ }
};

function Composer({ onClose, me, mode = 'post', editing, pickImages, initialHtml, onPosted }: CreatePostModalProps) {
  const isRecognition = editing ? editing.kind === 'recognition' : mode === 'recognition';
  const draftMode: ComposerMode = isRecognition ? 'recognition' : 'post';
  const useDraft = !editing && !initialHtml;
  const [draft] = useState(() => (useDraft ? loadDraft(me.id, draftMode) : null));
  // Chỉ có "Toàn tổ chức": quản lý dự án đã chốt không làm Trang cá nhân / Nhóm
  const [audiences] = useState<Audience[]>(['organization']);
  const [title, setTitle] = useState(editing?.title || draft?.title || '');
  const [content, setContent] = useState(editing?.html || initialHtml || draft?.content || '');
  const [images, setImages] = useState<PostImage[]>(editing?.imageMeta || []);
  /** url ảnh đã tải → dấu nhận biết tệp gốc */
  const imageSigs = useRef<Record<string, string>>({});
  const [commentsLocked, setCommentsLocked] = useState(editing?.commentsLocked || draft?.commentsLocked || false);
  const [people, setPeople] = useState<number[]>(() => (editing?.recognition?.people || []).map(p => Number(p.id)).filter(Number.isFinite).concat(draft?.people || []));
  const [badge, setBadge] = useState(editing?.recognition?.badge || draft?.badge || BADGES[0].key);
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(false);

  const editingScheduled = !!editing?.scheduled;
  const [publishAt, setPublishAt] = useState<Dayjs | null>(editingScheduled ? dayjs(editing!.publishAt) : null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [draftDate, setDraftDate] = useState<Dayjs>(publishAt || dayjs().add(1, 'hour').startOf('hour'));
  const [draftTime, setDraftTime] = useState<Dayjs>(publishAt || dayjs().add(1, 'hour').startOf('hour'));

  const fileRef = useRef<HTMLInputElement>(null);
  const { list: staff, loading: staffLoading } = useStaffOptions(true);
  // trình soạn thảo chỉ khởi tạo một lần, nên gợi ý @ đọc danh sách mới nhất qua ref
  const editorRef = useRef<any>(null);
  // Trình soạn thảo chuẩn hoá lại HTML (vd "à" → "&agrave;") nên so với bản vừa nạp, không so với bản API trả về
  const editorInitialHtml = useRef('');
  const staffRef = useRef<StaffOption[]>(staff);
  staffRef.current = staff;

  useEffect(() => { if (pickImages) fileRef.current?.click(); }, [pickImages]);

  const text = htmlToText(content);
  const hasContent = !!(text || title.trim() || images.length || people.length);
  const canSubmit = isRecognition ? people.length > 0 : !!(text || images.length);
  // ảnh chụp trạng thái ban đầu để biết bài đang sửa có thay đổi chưa
  const snapshot = JSON.stringify([title.trim(), content, images.map(i => i.url), audiences, people, badge, commentsLocked, publishAt?.valueOf()]);
  const [initialSnapshot] = useState(snapshot);
  const changed = snapshot !== initialSnapshot;

  // tự lưu nháp khi soạn bài mới
  useEffect(() => {
    if (!useDraft) return;
    saveDraft(me.id, draftMode, (text || title.trim() || people.length) ? { title, content, audiences, people, badge, commentsLocked } : null);
  }, [useDraft, me.id, draftMode, text, title, content, audiences, people, badge, commentsLocked]);

  const discardDraft = () => {
    saveDraft(me.id, draftMode, null);
    setTitle(''); setContent(''); setImages([]); setPeople([]); setCommentsLocked(false);
  };

  const close = () => {
    if (editing && changed) {
      Modal.confirm({ title: 'Bỏ các thay đổi?', content: 'Những gì bạn vừa sửa trong bài sẽ không được lưu.', okText: 'Bỏ thay đổi', okButtonProps: { danger: true }, cancelText: 'Tiếp tục sửa', onOk: onClose });
      return;
    }
    if (useDraft && hasContent) {
      if (images.length) message.info('Đã lưu nháp nội dung (ảnh cần chọn lại khi mở lại)');
      else if (text || title.trim() || people.length) message.info('Đã lưu nháp, mở lại để viết tiếp');
    }
    onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !showSchedule) close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_IMAGES - images.length;
    if (room <= 0) { message.warning(`Tối đa ${MAX_IMAGES} ảnh mỗi bài`); return; }
    const picked = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (picked.length < files.length) message.warning('Hiện chỉ hỗ trợ đính kèm ảnh');
    if (picked.length > room) message.warning(`Chỉ thêm được ${room} ảnh nữa`);
    // Cùng tên, dung lượng, ngày sửa là cùng một ảnh: bỏ qua để bài không có hai ảnh giống hệt
    const sigOf = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
    const taken = new Set(Object.values(imageSigs.current));
    const fresh = picked.filter(f => { const s = sigOf(f); if (taken.has(s)) return false; taken.add(s); return true; });
    if (fresh.length < picked.length) message.warning('Ảnh này đã được chọn, không thêm lại');
    const batch = fresh.slice(0, room);
    if (fileRef.current) fileRef.current.value = '';
    if (!batch.length) return;
    // API từ chối cả lượt nếu một tệp sai, nên báo ngay từ đây
    if (batch.some(f => f.size > MAX_IMAGE_SIZE)) { message.error('Ảnh quá lớn (tối đa 10MB)'); return; }
    setBusy(true);
    try {
      const uploaded = await feedApi.uploadImages(batch);
      uploaded.forEach((img, i) => { if (batch[i]) imageSigs.current[img.url] = sigOf(batch[i]); });
      setImages(prev => [...prev, ...uploaded]);
    } catch (e: any) {
      message.error(e?.message || 'Không tải được ảnh');
    } finally {
      setBusy(false);
    }
  };

  const confirmSchedule = () => {
    const at = draftDate.hour(draftTime.hour()).minute(draftTime.minute()).second(0).millisecond(0);
    if (!at.isAfter(dayjs().add(1, 'minute'))) { message.error('Thời gian hẹn đăng phải sau thời điểm hiện tại'); return; }
    setPublishAt(at);
    setShowSchedule(false);
  };

  const submit = async () => {
    if (!canSubmit) {
      message.warning(isRecognition ? 'Hãy chọn ít nhất một người được ghi nhận' : 'Hãy nhập nội dung');
      return;
    }
    // đọc thẳng từ trình soạn thảo để chắc chắn lấy được những gì vừa gõ; @nhắc tên nằm sẵn trong span[data-mention-id]
    const html: string = editorRef.current?.getContent?.() ?? content;
    const iso = publishAt?.toISOString();
    let patch: PostPatch | null = null;
    if (editing) {
      // Chỉ gửi trường đã đổi: API ghi "đã chỉnh sửa" kể cả khi body rỗng, và gửi lại html thì @nhắc tên bị kiểm tra lại
      patch = {};
      if (title.trim() !== (editing.title || '')) patch.title = title.trim();
      if (html !== editorInitialHtml.current) patch.html = html;
      if (images.map(i => i.url).join() !== editing.images.join()) patch.images = images;
      if (commentsLocked !== editing.commentsLocked) patch.commentsLocked = commentsLocked;
      if (editingScheduled && publishAt?.valueOf() !== dayjs(editing.publishAt).valueOf()) patch.publishAt = iso ?? null;
      if (isRecognition) {
        // API không giữ thứ tự người được ghi nhận nên so sau khi sắp xếp
        const sortedIds = (ids: number[]) => [...ids].sort((a, b) => a - b).join();
        if (sortedIds(people) !== sortedIds((editing.recognition?.people || []).map(p => Number(p.id)))) patch.staffIds = people;
        if (badge !== editing.recognition?.badge) patch.badge = badge;
      }
      if (!Object.keys(patch).length) { onClose(); return; }
    }
    setPosting(true);
    try {
      const { post, message: msg } = editing && patch
        ? await feedApi.update(editing.id, patch)
        : isRecognition
          ? await feedApi.createRecognition({ staffIds: people, badge, html, images, commentsLocked, publishAt: iso })
          : await feedApi.create({ title, html, images, commentsLocked, publishAt: iso });
      if (useDraft) saveDraft(me.id, draftMode, null);
      onPosted?.(post);
      message.success(post.scheduled ? `Đã hẹn đăng lúc ${dayjs(post.publishAt).format('HH:mm DD/MM/YYYY')}` : msg);
      onClose();
    } catch (e: any) {
      // giữ hộp mở, nội dung còn nguyên để sửa rồi đăng lại
      message.error(e?.message || 'Không lưu được bài viết');
    } finally {
      setPosting(false);
    }
  };

  const audienceItems: MenuProps['items'] = AUDIENCES.map(a => ({
    key: a.key,
    label: (
      <div className="flex items-center justify-between gap-4 py-1">
        <div className="flex items-center gap-2">
          <div className={`w-5 h-5 rounded-full ${a.bg} text-white flex items-center justify-center`}><a.icon className="w-3 h-3" /></div>
          <span className="font-medium">{AUDIENCE_LABEL[a.key]}</span>
        </div>
        {audiences.includes(a.key) && <Check className="w-4 h-4 text-cyan-600" />}
      </div>
    ),
  }));

  // người đã được ghi nhận trước đó có thể không còn trong danh sách nhân sự: vẫn hiện tên thay vì mã số
  const peopleOptions = [
    ...staff.map(s => ({ value: s.value, label: s.label, desc: s.desc, avatar: s.avatar })),
    ...(editing?.recognition?.people || [])
      .filter(p => !staff.some(s => s.value === Number(p.id)))
      .map(p => ({ value: Number(p.id), label: p.name, desc: undefined, avatar: p.avatar })),
  ];

  const submitLabel = editingScheduled && !publishAt ? 'Đăng ngay' : editing ? 'Lưu thay đổi' : publishAt ? 'Lên lịch' : isRecognition ? 'Gửi ghi nhận' : 'Đăng';

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm p-4" onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] relative" role="dialog" aria-modal="true">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <UserAvatar name={me.name} src={me.avatar} />
            <div>
              <span className="font-bold text-gray-900 dark:text-gray-100 block text-sm">
                {me.name}
                <span className="font-normal text-gray-500"> · {editing ? 'Chỉnh sửa bài viết' : isRecognition ? 'Ghi nhận đồng nghiệp' : 'Tạo bài viết'}</span>
              </span>
              <Dropdown menu={{ items: audienceItems }} trigger={['click']} placement="bottomLeft">
                <button className="flex items-center space-x-1.5 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 px-2.5 py-1 rounded-md font-medium transition mt-0.5 cursor-pointer">
                  <Globe className="w-3 h-3" />
                  <span>{audiences.map(a => AUDIENCE_LABEL[a]).join(', ')}</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
              </Dropdown>
            </div>
          </div>
          <button onClick={close} aria-label="Đóng" className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 flex items-center justify-center transition cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 md:px-6 py-4 overflow-y-auto space-y-4 flex-1">
          {!editing && draft && hasContent && (
            <div className="flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2">
              <span>📝 Đã khôi phục bản nháp bạn đang viết dở.</span>
              <button type="button" onClick={discardDraft} className="font-bold text-red-500 hover:underline cursor-pointer">Xóa nháp</button>
            </div>
          )}
          {isRecognition ? (
            <div className="space-y-3 rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 p-4">
              <div>
                <div className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-1.5">Ghi nhận ai? <span className="text-red-500">*</span></div>
                <Select
                  mode="multiple"
                  className="w-full"
                  placeholder="Tìm theo tên nhân sự"
                  value={people}
                  onChange={setPeople}
                  loading={staffLoading}
                  notFoundContent={staffLoading ? <Spin size="small" /> : 'Không tìm thấy nhân sự'}
                  optionFilterProp="label"
                  maxCount={50}
                  options={peopleOptions}
                  optionRender={o => (
                    <div className="flex items-center gap-2">
                      <UserAvatar name={String(o.data.label)} src={(o.data as any).avatar} size={24} />
                      <div className="min-w-0">
                        <div className="text-sm">{o.data.label}</div>
                        {(o.data as any).desc && <div className="text-[11px] text-gray-500 truncate">{(o.data as any).desc}</div>}
                      </div>
                    </div>
                  )}
                />
              </div>
              <div>
                <div className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-1.5">Huy hiệu</div>
                <div className="flex flex-wrap gap-2">
                  {BADGES.map(b => (
                    <button key={b.key} type="button" onClick={() => setBadge(b.key)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition cursor-pointer ${badge === b.key ? 'border-amber-500 bg-white dark:bg-gray-800 font-bold text-amber-700 dark:text-amber-400 shadow-sm' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'}`}>
                      {b.emoji} {b.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Tiêu đề (không bắt buộc)"
              className="w-full text-lg font-bold bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 border-b border-gray-100 dark:border-gray-700 pb-2"
            />
          )}

          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <Editor
              // Bản self-host (GPL) trong public/tinymce — không cần API key cloud
              tinymceScriptSrc={`${process.env.PUBLIC_URL || ''}/tinymce/tinymce.min.js`}
              licenseKey="gpl"
              value={content}
              onEditorChange={setContent}
              onInit={(_, editor) => { editorInitialHtml.current = editor.getContent(); }}
              init={{
                height: isRecognition ? 180 : 220,
                menubar: false,
                branding: false,
                resize: false,
                skin: document.documentElement.classList.contains('dark') ? 'oxide-dark' : 'oxide',
                content_css: document.documentElement.classList.contains('dark') ? 'dark' : 'default',
                plugins: ['link', 'lists', 'table', 'code', 'fullscreen', 'preview', 'emoticons'],
                toolbar: 'undo redo | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | link emoticons | preview fullscreen',
                placeholder: isRecognition ? 'Viết vài lời cảm ơn / ghi nhận...' : 'Bạn muốn chia sẻ điều gì?',
                paste_data_images: false,
                extended_valid_elements: 'span[class|data-mention-id|data-user-id|contenteditable]',
                content_style: '.mention{color:#16a34a;font-weight:600;background:rgba(22,163,74,.08);border-radius:4px;padding:0 2px}',
                // Gõ @ để nhắc tên đồng nghiệp
                setup: (editor: any) => {
                  editorRef.current = editor;
                  // tinymce-react chỉ nghe change/keyup: bàn phím điện thoại (không có keyup) sẽ làm state chậm → nghe thêm input
                  editor.on('input', () => setContent(editor.getContent()));
                  editor.ui.registry.addAutocompleter('staff-mention', {
                    trigger: '@',
                    minChars: 0,
                    columns: 1,
                    fetch: (pattern: string) => Promise.resolve(searchStaff(staffRef.current, pattern).map(s => ({
                      type: 'autocompleteitem', value: String(s.value), text: s.name + (s.desc ? ` — ${s.desc}` : ''),
                    }))),
                    onAction: (api: any, rng: Range, value: string) => {
                      const s = staffRef.current.find(x => String(x.value) === value);
                      if (!s) return;
                      const esc = (t: string) => t.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
                      editor.selection.setRng(rng);
                      editor.insertContent(`<span class="mention" contenteditable="false" data-mention-id="${s.value}" data-user-id="${s.userId ?? ''}">@${esc(s.name)}</span>&nbsp;`);
                      api.hide();
                    },
                  });
                },
              }}
            />
          </div>
          <div className="-mt-2 text-[11px] text-gray-400">Mẹo: gõ <b>@</b> rồi tên để nhắc đồng nghiệp — họ sẽ thấy bài trong mục “Nhắc đến tôi”.</div>

          {(images.length > 0 || busy) && (
            <div className="grid grid-cols-3 gap-2">
              {images.map((img, i) => (
                <div key={img.url} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                  <img src={img.url} alt={`Ảnh ${i + 1}`} className="w-full h-full object-cover" />
                  <button onClick={() => { delete imageSigs.current[img.url]; setImages(images.filter((_, j) => j !== i)); }} title="Bỏ ảnh"
                    className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center cursor-pointer">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {busy && <div className="aspect-square rounded-lg border border-dashed border-gray-300 flex items-center justify-center"><Spin /></div>}
            </div>
          )}

          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-3 flex items-center justify-between flex-wrap gap-2 bg-gray-50 dark:bg-gray-700">
            <div className="flex items-center gap-1">
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={e => addFiles(e.target.files)} />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={images.length >= MAX_IMAGES || busy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-600 disabled:opacity-50 cursor-pointer">
                <ImageIcon className="w-4 h-4 text-blue-500" /> Ảnh ({images.length}/{MAX_IMAGES})
              </button>
              {isRecognition && <span className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold text-amber-600"><Award className="w-4 h-4" /> Ghi nhận</span>}
            </div>
            <label className="flex items-center space-x-2 cursor-pointer select-none">
              <input type="checkbox" checked={commentsLocked} onChange={e => setCommentsLocked(e.target.checked)} className="w-4 h-4 accent-cyan-600 rounded" />
              <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">Chặn và ẩn bình luận</span>
            </label>
          </div>

          {publishAt && (
            <div className="flex items-center justify-between gap-2 text-sm text-cyan-800 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-900/20 p-3 rounded-xl border border-cyan-200 dark:border-cyan-800">
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Hẹn đăng lúc <strong>{publishAt.format('HH:mm, DD/MM/YYYY')}</strong></span>
              <button onClick={() => setPublishAt(null)} className="text-xs font-bold hover:underline cursor-pointer">{editingScheduled ? 'Đăng ngay khi lưu' : 'Bỏ hẹn giờ'}</button>
            </div>
          )}
        </div>

        <div className="gap-3 px-4 md:px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex items-center justify-between flex-shrink-0">
          <button onClick={submit} disabled={!canSubmit || busy || posting}
            className="flex-1 px-8 py-2.5 bg-sky-900 hover:bg-sky-950 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold rounded-xl transition shadow-md cursor-pointer">
            {posting ? (editing ? 'Đang lưu...' : 'Đang đăng...') : submitLabel}
          </button>
          {(!editing || editingScheduled) && (
            <button onClick={() => setShowSchedule(true)} title="Lên lịch đăng bài"
              className={`w-[44px] h-[44px] rounded-lg flex items-center justify-center transition cursor-pointer ${publishAt ? 'bg-cyan-600 text-white' : 'bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 text-gray-600 dark:text-gray-200'}`}>
              <CalendarIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {showSchedule && (
          <div className="absolute inset-0 z-20 bg-white dark:bg-gray-800 flex flex-col rounded-2xl overflow-hidden">
            <div className="px-4 py-3.5 border-b border-gray-200 dark:border-gray-700 flex items-center space-x-3">
              <button onClick={() => setShowSchedule(false)} className="w-8 h-8 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 cursor-pointer">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <span className="font-bold text-gray-900 dark:text-gray-100 text-base">Đặt lịch bài viết</span>
            </div>
            <div className="p-6 space-y-6 flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Ngày đăng</label>
                  <DatePicker value={draftDate} onChange={d => d && setDraftDate(d)} format="DD/MM/YYYY" allowClear={false}
                    disabledDate={d => d.isBefore(dayjs(), 'day')} className="w-full h-11 rounded-xl" getPopupContainer={t => t.parentElement || document.body} />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Giờ đăng</label>
                  <TimePicker value={draftTime} onChange={t => t && setDraftTime(t)} format="HH:mm" minuteStep={5} allowClear={false} needConfirm={false}
                    className="w-full h-11 rounded-xl" getPopupContainer={t => t.parentElement || document.body} />
                </div>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700">
                <CalendarIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span>Bài viết sẽ được đăng vào <strong>{draftTime.format('HH:mm')}</strong>, ngày <strong>{draftDate.format('DD/MM/YYYY')}</strong>. Bạn xem và sửa bài hẹn giờ ở mục “Bài viết hẹn giờ”.</span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
              <button onClick={() => { setPublishAt(null); setShowSchedule(false); }} className="px-4 py-2 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl cursor-pointer">Hủy đặt lịch</button>
              <button onClick={confirmSchedule} className="px-6 py-2.5 bg-sky-900 hover:bg-sky-950 text-white font-bold text-sm rounded-xl shadow-md cursor-pointer">Xác nhận</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
